import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { processPLData, PLRow } from './utils/plDataProcessor';

interface PLTabProps {
  data: any[];
  companyId: number;
  selectedValuationLevel?: number;
}

const PLTab: React.FC<PLTabProps> = ({ data, companyId, selectedValuationLevel }) => {
  const [processedData, setProcessedData] = useState<PLRow[]>([]);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [currency, setCurrency] = useState<string>('USD');
  const [yearPeriodTypes, setYearPeriodTypes] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (data && data.length > 0) {
      const {
        rows,
        years,
        currency: detectedCurrency,
        yearPeriodTypes: periodTypes,
      } = processPLData(data, selectedValuationLevel);
      setProcessedData(rows);
      setAvailableYears(years);
      setCurrency(detectedCurrency);
      setYearPeriodTypes(periodTypes);
    }
  }, [data, selectedValuationLevel]);

  // Format currency values with dynamic currency
  const formatCurrency = (value: number): string => {
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Get period type indicator for year header
  const getPeriodTypeIndicator = (year: string): string => {
    const periodType = yearPeriodTypes.get(year);
    if (!periodType) return '';

    const firstChar = periodType.toLowerCase().charAt(0);
    switch (firstChar) {
      case 'a':
        return 'a'; // actual
      case 'p':
        return 'p'; // plan
      case 'b':
        return 'b'; // budget
      case 'f':
        return 'f'; // forecast
      default:
        return '';
    }
  };

  // Format percentage values
  const formatPercentage = (value: number): string => {
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Format growth rate as small percentage
  const formatGrowthRate = (value: number): string => {
    if (!value || value === 0) return '';
    const sign = value > 0 ? '+' : '';
    return `${sign}${(value * 100).toFixed(1)}%`;
  };

  // Check if a row has only zero values across all years
  const hasOnlyZeroValues = (row: PLRow): boolean => {
    return availableYears.every(year => !row.values[year] || row.values[year] === 0);
  };

  if (!data || data.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
        sx={{ height: '400px', textAlign: 'center' }}
      >
        <Alert severity="info" sx={{ mb: 2, maxWidth: '500px' }}>
          <Typography variant="h6" gutterBottom>
            No Profit & Loss Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This company does not have any P&L financial data uploaded to the system yet. To view financial analysis,
            please upload the company's financial statements through the benchmarking section.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Box mb={2}>
        <Typography variant="subtitle1" color="text.secondary">
          Profit & Loss Statement - All values in {currency}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Financial data with calculated margins and ratios
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  minWidth: 250,
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: 'var(--surface-raised)',
                }}
              >
                Item
              </TableCell>
              {availableYears.map(year => (
                <TableCell
                  key={year}
                  align="right"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'var(--surface-raised)',
                    minWidth: 120,
                  }}
                >
                  {year}
                  {getPeriodTypeIndicator(year)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((row, index) => (
              <TableRow
                key={row.id}
                sx={{
                  backgroundColor: row.level === 0 ? 'var(--surface-raised)' : 'transparent',
                  '&:hover': {
                    backgroundColor: row.level === 0 ? 'var(--surface-overlay)' : 'var(--surface-inset)',
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: row.level === 0 ? 'bold' : 'normal',
                    fontSize: row.level === 1 ? '0.85rem' : 'inherit',
                    paddingLeft: row.level === 0 ? 1 : 3,
                    borderBottom: row.id.includes('_margin') ? '1px solid var(--border-subtle)' : 'none',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: row.level === 0 ? 'var(--surface-raised)' : 'var(--surface-base)',
                    zIndex: 1,
                  }}
                >
                  {row.label}
                </TableCell>
                {availableYears.map(year => (
                  <TableCell
                    key={year}
                    align="right"
                    sx={{
                      fontWeight: row.level === 0 ? 'bold' : 'normal',
                      fontSize: row.level === 1 ? '0.85rem' : 'inherit',
                      color: row.values[year] < 0 ? '#d32f2f' : 'inherit',
                      borderBottom: row.id.includes('_margin') ? '1px solid var(--border-subtle)' : 'none',
                    }}
                  >
                    {row.format === 'percentage' ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box
                          sx={{
                            fontSize: row.level === 1 ? '0.85rem' : '0.9rem',
                            color: 'var(--text-secondary)',
                            fontWeight: row.level === 0 ? 'bold' : 'normal',
                          }}
                        >
                          {formatPercentage(row.values[year] || 0)}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Box>{formatCurrency(row.values[year] || 0)}</Box>
                        {row.showGrowthRate && row.growthRates && row.growthRates[year] && !hasOnlyZeroValues(row) && (
                          <Box
                            sx={{
                              fontSize: '0.7rem',
                              color:
                                row.growthRates[year] > 0
                                  ? '#4caf50'
                                  : row.growthRates[year] < 0
                                    ? '#f44336'
                                    : 'var(--text-secondary)',
                              fontWeight: 'normal',
                              mt: 0.25,
                            }}
                          >
                            {formatGrowthRate(row.growthRates[year])}
                          </Box>
                        )}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PLTab;
